import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * Syncs the custom subscription picker with the product add-to-cart form.
 *
 * @extends {Component}
 */
class D2cSubscriptionPicker extends Component {
  /** @type {HTMLElement | null} */
  #boundProductFormComponent = null;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onPlanAreaClick);
    this.#bindProductForm();
    this.#applyPerDoseSuffix();
    this.#normalizeSelection();
    this.#ensureDefaultSelection();
    this.#updateActivePlanClasses();
    this.#syncSellingPlanWithRetry();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }

    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onPlanAreaClick);
    this.#unbindProductForm();
  }

  /** @param {Event} event */
  #onPlanAreaClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.d2c-subscription__plan-input')) return;

    const plan = target.closest('.d2c-subscription__plan');
    if (!(plan instanceof HTMLElement) || plan.classList.contains('d2c-subscription__plan--one-time')) return;

    const radio = plan.querySelector('.d2c-subscription__plan-input');
    if (!(radio instanceof HTMLInputElement) || radio.checked) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target instanceof HTMLSelectElement && event.target.classList.contains('d2c-subscription__plan-select')) {
      this.#onPlanSelectChange(event.target);
      return;
    }

    this.#normalizeSelection();
    this.#updateActivePlanClasses();
    this.#syncSellingPlanWithRetry();
  };

  /**
   * @param {HTMLSelectElement} select
   */
  #onPlanSelectChange(select) {
    const plan = select.closest('.d2c-subscription__plan');
    if (!(plan instanceof HTMLElement)) return;

    const option = select.selectedOptions[0];
    if (!option) return;

    const planId = option.value;
    const discountPercent = option.dataset.discount ?? '0';
    const discountLabel = `${discountPercent}%`;

    const radio = plan.querySelector('.d2c-subscription__plan-input');
    if (radio instanceof HTMLInputElement) {
      radio.dataset.sellingPlanId = planId;
    }

    plan.dataset.sellingPlanId = planId;

    const priceElement = plan.querySelector('.d2c-subscription__price');
    if (priceElement && option.dataset.priceFormatted) {
      priceElement.textContent = option.dataset.priceFormatted;
    }

    const compareElement = plan.querySelector('.d2c-subscription__compare-price');
    if (compareElement instanceof HTMLElement) {
      const showCompare = option.dataset.showCompare === 'true';
      compareElement.hidden = !showCompare;
      if (showCompare && option.dataset.compareFormatted) {
        compareElement.textContent = option.dataset.compareFormatted;
      }
    }

    const perDoseElement = plan.querySelector('.d2c-subscription__per-dose--subscribe');
    const perDoseValueElement = plan.querySelector('.d2c-subscription__per-dose-value');
    if (perDoseElement instanceof HTMLElement && perDoseValueElement instanceof HTMLElement) {
      const perDoseFormatted = option.dataset.perDoseFormatted?.trim();
      if (perDoseFormatted) {
        perDoseElement.hidden = false;
        perDoseValueElement.textContent = perDoseFormatted;
      } else {
        perDoseElement.hidden = true;
      }
    }

    this.#updateBenefitsFromTemplate(plan, discountLabel);

    if (radio instanceof HTMLInputElement && radio.checked) {
      this.#syncSellingPlanWithRetry();
    }
  }

  /**
   * @param {HTMLElement} plan
   * @param {string} discountLabel
   */
  #updateBenefitsFromTemplate(plan, discountLabel) {
    const templateElement = plan.querySelector('.d2c-subscription__benefits-source');
    const benefitsContainer = plan.querySelector('[data-benefits-container]');
    if (!(templateElement instanceof HTMLTemplateElement) || !(benefitsContainer instanceof HTMLElement)) return;

    const templateText = templateElement.content.textContent ?? '';
    const benefitsText = templateText.replace(/\[discount\]/g, discountLabel);
    const lines = benefitsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    benefitsContainer.replaceChildren(
      ...lines.map((line) => {
        const paragraph = document.createElement('p');
        paragraph.className = 'd2c-subscription__benefit';
        paragraph.textContent = line;
        return paragraph;
      })
    );
  }

  #updateActivePlanClasses() {
    this.querySelectorAll('.d2c-subscription__plan').forEach((plan) => {
      if (!(plan instanceof HTMLElement)) return;
      const isActive = Boolean(plan.querySelector('.d2c-subscription__plan-input:checked'));
      plan.classList.toggle('d2c-subscription__plan--active', isActive);

      const frequency = plan.querySelector('.d2c-subscription__plan-frequency');
      if (frequency instanceof HTMLElement) {
        frequency.hidden = !isActive;
      }
    });
  }

  #onProductFormSubmit = () => {
    this.#syncSellingPlan();
  };

  /** @param {Event} event */
  #onProductFormPointerDown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[ref="addToCartButton"], .add-to-cart-button, button[type="submit"]')) {
      this.#syncSellingPlan();
    }
  };

  #bindProductForm() {
    const productFormComponent = this.#getProductFormComponent();
    if (!productFormComponent || productFormComponent === this.#boundProductFormComponent) return;

    this.#unbindProductForm();
    this.#boundProductFormComponent = productFormComponent;
    productFormComponent.addEventListener('submit', this.#onProductFormSubmit, { capture: true });
    productFormComponent.addEventListener('pointerdown', this.#onProductFormPointerDown, { capture: true });
  }

  #unbindProductForm() {
    if (!this.#boundProductFormComponent) return;

    this.#boundProductFormComponent.removeEventListener('submit', this.#onProductFormSubmit, { capture: true });
    this.#boundProductFormComponent.removeEventListener('pointerdown', this.#onProductFormPointerDown, {
      capture: true,
    });
    this.#boundProductFormComponent = null;
  }

  #applyPerDoseSuffix() {
    const suffix = this.dataset.perDoseSuffix?.trim();
    if (!suffix) return;

    this.querySelectorAll('.d2c-subscription__per-dose-suffix').forEach((element) => {
      element.textContent = suffix;
    });
  }

  #normalizeSelection() {
    const checkedRadios = this.querySelectorAll('input[type="radio"]:checked');
    if (checkedRadios.length <= 1) return;

    /** @type {HTMLInputElement | undefined} */
    const keep = checkedRadios[0] instanceof HTMLInputElement ? checkedRadios[0] : undefined;
    checkedRadios.forEach((radio, index) => {
      if (index === 0 || !(radio instanceof HTMLInputElement)) return;
      radio.checked = false;
    });

    if (keep) keep.checked = true;
  }

  #ensureDefaultSelection() {
    if (this.querySelector('input[type="radio"]:checked')) return;

    const defaultPlan = this.querySelector('.d2c-subscription__plan-input[data-default-selected="true"]');
    if (defaultPlan instanceof HTMLInputElement) {
      defaultPlan.checked = true;
      return;
    }

    const oneTime = this.querySelector('.d2c-subscription__one-time-input');
    if (oneTime instanceof HTMLInputElement) {
      oneTime.checked = true;
      return;
    }

    const firstPlan = this.querySelector(
      '.d2c-subscription__plan-input:not(.d2c-subscription__one-time-input)'
    );
    if (firstPlan instanceof HTMLInputElement) {
      firstPlan.checked = true;
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getScope() {
    return this.closest('.product-details, .shopify-section, dialog');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductFormComponent() {
    const scope = this.#getScope();
    if (!scope) return null;

    const productId = this.dataset.productId;
    const productFormComponent = productId
      ? scope.querySelector(`product-form-component[data-product-id="${productId}"]`)
      : scope.querySelector('product-form-component');

    return productFormComponent instanceof HTMLElement ? productFormComponent : null;
  }

  /**
   * @returns {HTMLFormElement | null}
   */
  #getProductForm() {
    const productFormComponent = this.#getProductFormComponent();
    if (!productFormComponent) return null;

    const form = productFormComponent.querySelector('form[data-type="add-to-cart-form"]');
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getOrCreateFormSellingPlanInput() {
    const form = this.#getProductForm();
    if (!form) return null;

    let input = form.querySelector('input[name="selling_plan"][data-d2c-subscription-sync]');
    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.setAttribute('data-d2c-subscription-sync', 'true');
      form.appendChild(input);
    }

    return input;
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  #findAppSellingPlanInputs() {
    const scope = this.#getScope();
    if (!scope) return [];

    /** @type {HTMLInputElement[]} */
    const inputs = [];
    scope.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
      if (
        input instanceof HTMLInputElement &&
        !this.contains(input) &&
        !input.hasAttribute('data-d2c-subscription-sync')
      ) {
        inputs.push(input);
      }
    });

    return inputs;
  }

  /**
   * @param {HTMLInputElement} input
   */
  #dispatchInputEvents(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {HTMLInputElement | null} formInput
   * @param {string | undefined} planId
   */
  #updateFormSellingPlanInput(formInput, planId) {
    if (!formInput) return;

    if (!planId) {
      formInput.value = '';
      formInput.disabled = true;
      this.#dispatchInputEvents(formInput);
      return;
    }

    formInput.disabled = false;
    if (formInput.value !== String(planId)) {
      formInput.value = String(planId);
      this.#dispatchInputEvents(formInput);
    }
  }

  /**
   * @returns {boolean}
   */
  #syncSellingPlan() {
    this.#bindProductForm();

    const selected = this.querySelector('input[type="radio"]:checked');
    const planId = selected instanceof HTMLInputElement ? selected.dataset.sellingPlanId : undefined;
    const isOneTime = selected instanceof HTMLInputElement && selected.value === 'one-time';
    const formInput = this.#getOrCreateFormSellingPlanInput();

    if (isOneTime) {
      this.#updateFormSellingPlanInput(formInput, undefined);

      this.#findAppSellingPlanInputs().forEach((input) => {
        input.value = '';
        input.disabled = true;
        this.#dispatchInputEvents(input);
      });

      return formInput !== null;
    }

    if (!planId) return false;

    this.#updateFormSellingPlanInput(formInput, planId);

    this.#findAppSellingPlanInputs().forEach((input) => {
      input.disabled = false;
      if (input.value !== String(planId)) {
        input.value = String(planId);
        this.#dispatchInputEvents(input);
      }
    });

    return formInput !== null;
  }

  /**
   * @param {number} attempt
   */
  #syncSellingPlanWithRetry(attempt = 0) {
    const synced = this.#syncSellingPlan();

    if (!synced && attempt < 15) {
      window.setTimeout(() => this.#syncSellingPlanWithRetry(attempt + 1), 100 * (attempt + 1));
    }
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const newPicker = event.detail.data.html.querySelector(`d2c-subscription-picker[data-block-id="${blockId}"]`);
    if (!newPicker) return;

    const selectedRadio = this.querySelector('input[type="radio"]:checked');
    const selectedValue = selectedRadio instanceof HTMLInputElement ? selectedRadio.value : undefined;
    const planSelectBeforeUpdate = this.querySelector('.d2c-subscription__plan-select');
    const selectedPlanId =
      (planSelectBeforeUpdate instanceof HTMLSelectElement ? planSelectBeforeUpdate.value : undefined) ??
      (selectedRadio instanceof HTMLInputElement ? selectedRadio.dataset.sellingPlanId : undefined);

    this.innerHTML = newPicker.innerHTML;

    if (selectedValue) {
      const radio = this.querySelector(`input[type="radio"][value="${CSS.escape(selectedValue)}"]`);
      if (radio instanceof HTMLInputElement) {
        radio.checked = true;
      }
    } else if (selectedPlanId) {
      const radio = this.querySelector(`input[type="radio"][data-selling-plan-id="${selectedPlanId}"]`);
      if (radio instanceof HTMLInputElement) {
        radio.checked = true;
      }
    }

    const planSelect = this.querySelector('.d2c-subscription__plan-select');
    if (planSelect instanceof HTMLSelectElement && selectedPlanId) {
      const matchingOption = Array.from(planSelect.options).find((option) => option.value === selectedPlanId);
      if (matchingOption) {
        planSelect.value = selectedPlanId;
        this.#onPlanSelectChange(planSelect);
      }
    }

    this.#bindProductForm();
    this.#applyPerDoseSuffix();
    this.#normalizeSelection();
    this.#ensureDefaultSelection();
    this.#updateActivePlanClasses();
    this.#syncSellingPlanWithRetry();
  };
}

if (!customElements.get('d2c-subscription-picker')) {
  customElements.define('d2c-subscription-picker', D2cSubscriptionPicker);
}
